import React, { useEffect, useState } from "react";
import { Button, ControlLabel, Table } from 'react-bootstrap';
import Alert from 'react-s-alert';
import OverdueSalesEmailsService from '../../utils/OverdueSalesEmailsService';

// Admin page for the two overdue sales emails that go out every day at 8am:
// the sales rep email (to each rep with overdue sales) and the management email.
// Dan always receives the management email; this is where the additional
// dashboard users that also receive it are nominated.
const OverdueSalesEmails = () => {
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);

  const load = async () => {
    const response = await OverdueSalesEmailsService.get();
    setSettings(response);
  };

  useEffect(() => { load(); }, []);

  const isNominated = (email) => settings.manager_recipients.includes((email || '').toLowerCase());

  const toggleRecipient = (email) => {
    const address = (email || '').toLowerCase();
    const recipients = isNominated(address)
      ? settings.manager_recipients.filter((e) => e !== address)
      : [...settings.manager_recipients, address];
    setSettings({ ...settings, manager_recipients: recipients });
  };

  const save = async () => {
    setSaving(true);
    try {
      const response = await OverdueSalesEmailsService.update({
        enabled: settings.enabled,
        manager_recipients: settings.manager_recipients,
        notion_process_url: settings.notion_process_url
      });
      setSettings(response);
      Alert.success("Overdue sales email settings saved.");
    } catch (e) {
      Alert.error("Could not save the settings.");
    }
    setSaving(false);
  };

  const sendPreview = async () => {
    setSending(true);
    try {
      const response = await OverdueSalesEmailsService.preview();
      Alert.success(`Preview sent to ${response.sent_to}.`);
    } catch (e) {
      Alert.error((e && e.data && e.data.error) || "Could not send the preview.");
    }
    setSending(false);
  };

  if (!settings) return <div />;

  const nominated = settings.users.filter((u) => isNominated(u.email));

  return (
    <div>
      <h3 className="first-title mb-3">Overdue Sales Emails</h3>

      <div className="checkbox mt-0">
        <label>
          <input
            type="checkbox"
            checked={!!settings.enabled}
            onChange={(e) => setSettings({ ...settings, enabled: e.target.checked })}
          />
          {" "}Send these emails daily at 8am
        </label>
      </div>

      <div className="row">
        <div className="col-sm-6">
          <div className="form-group">
            <ControlLabel>Notion process link</ControlLabel>
            <input
              className="form-control"
              placeholder="https://www.notion.so/..."
              value={settings.notion_process_url || ''}
              onChange={(e) => setSettings({ ...settings, notion_process_url: e.target.value })}
            />
            <span className="help-block">
              The "please follow this process in notion" link in the sales rep email.
            </span>
          </div>
        </div>
      </div>

      <h4 className="mt-3">Management Email</h4>
      <p className="text-muted">
        <code>{settings.always_recipient}</code> always receives it. Tick any other dashboard users
        that should receive it too{nominated.length > 0 && <span> &mdash; currently {nominated.length} nominated</span>}.
      </p>

      <div className="row">
        <div className="col-sm-7">
          <div style={{ maxHeight: 420, overflowY: 'auto' }}>
            <Table striped condensed>
              <thead>
                <tr>
                  <th style={{ width: 40 }}></th>
                  <th>Name</th>
                  <th>Email</th>
                </tr>
              </thead>
              <tbody>
                {settings.users.map((user) => {
                  const always = (user.email || '').toLowerCase() === (settings.always_recipient || '').toLowerCase();
                  return (
                    <tr key={user.id}>
                      <td>
                        <input
                          type="checkbox"
                          checked={always || isNominated(user.email)}
                          disabled={always}
                          onChange={() => toggleRecipient(user.email)}
                        />
                      </td>
                      <td>{user.name}{always && <span className="text-muted"> (always)</span>}</td>
                      <td>{user.email}</td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          </div>
        </div>
      </div>

      <div className="mt-2 mb-4">
        <Button bsStyle="success" disabled={saving} onClick={save}>
          {saving ? "Saving..." : "Save Changes"}
        </Button>
        <Button bsStyle="default" className="ml-1" disabled={sending} onClick={sendPreview}>
          {sending ? "Sending..." : "Send me a preview"}
        </Button>
      </div>
    </div>
  );
};

export default OverdueSalesEmails;
